import { FontPoppins } from '@/constants/font';
import { Ionicons } from '@expo/vector-icons';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Control, useController } from 'react-hook-form';
import { View, Text, Pressable, Modal, ScrollView, StyleSheet, Platform } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ThemedText } from '../themed-text';
import { createThemedStyles } from '@/hooks/use-theme-colors';

type Option = {
  label: string;
  value: string;
};

/** Listas curtas (ex.: dias da semana) exibem todas as opções sem scroll. */
const OPTIONS_WITHOUT_SCROLL_MAX = 10;

type ControlledSelectProps = {
  control: Control<any>;
  name: string;
  label: string;
  options: Option[];
  placeholder?: string;
  isMultiple?: boolean;
  disabled?: boolean;
  noOpacityWhenDisabled?: boolean;
  /** Só multi: trava novas marcações ao atingir o limite (desmarcar sempre liberado). */
  maxSelected?: number;
  /** testID repassado ao botão do select */
  testID?: string;
  /** Abre o modal automaticamente ao montar */
  autoOpenOnMount?: boolean;
  /** Posição do popup: 'top' = acima do select (padrão, melhor no iPhone), 'center' = centralizado */
  dropdownPlacement?: 'top' | 'center';
};

export function ControlledSelect({
  control,
  name,
  label,
  options,
  placeholder = 'Selecione...',
  isMultiple = false,
  disabled = false,
  noOpacityWhenDisabled = false,
  maxSelected,
  testID,
  autoOpenOnMount = false,
  dropdownPlacement,
}: ControlledSelectProps) {
  const styles = useStyles();
  const placement = dropdownPlacement ?? (isMultiple ? 'center' : 'top');
  const triggerRef = useRef<View>(null);
  const insets = useSafeAreaInsets();
  const [triggerRect, setTriggerRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const { field } = useController({
    control,
    name,
  });

  /* Removed useThemeColor hooks */

  const [modalVisible, setModalVisible] = useState(false);

  const measureTriggerAndOpen = useCallback(() => {
    const node = triggerRef.current as any;
    if (!node) {
      setModalVisible(true);
      return;
    }
    if (Platform.OS === 'web') {
      const el = node.getBoundingClientRect ? node : (node?.getNativeScrollRef?.() ?? node);
      const rect =
        typeof el?.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
      if (rect) {
        setTriggerRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
      }
      setModalVisible(true);
      return;
    }
    {
      node.measureInWindow?.((x: number, y: number, width: number, height: number) => {
        setTriggerRect({ x, y, width, height });
        setModalVisible(true);
      });
      if (typeof node.measureInWindow !== 'function') setModalVisible(true);
    }
  }, []);

  useEffect(() => {
    if (autoOpenOnMount) {
      const t = setTimeout(() => {
        if (placement === 'top') measureTriggerAndOpen();
        else setModalVisible(true);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [autoOpenOnMount, placement, measureTriggerAndOpen]);
  const [selectedValues, setSelectedValues] = useState<string[]>(() =>
    isMultiple ? field.value || [] : [],
  );
  const [selectedValue, setSelectedValue] = useState<string | null>(() =>
    !isMultiple ? field.value || null : null,
  );

  // Sincroniza com o valor do formulário
  useEffect(() => {
    if (isMultiple) {
      setSelectedValues(field.value || []);
    } else {
      setSelectedValue(field.value || null);
    }
  }, [field.value, isMultiple]);

  const handleSelect = (value: string) => {
    if (disabled) return;
    if (isMultiple) {
      const alreadyOn = selectedValues.includes(value);
      if (!alreadyOn && maxSelected != null && selectedValues.length >= maxSelected) {
        return;
      }
      const newValues = alreadyOn
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      setSelectedValues(newValues);
      // Não atualiza o formulário imediatamente, só quando confirmar
    } else {
      setSelectedValue(value);
      field.onChange(value);
      setModalVisible(false);
      field.onBlur();
    }
  };

  const handleConfirmMultiple = () => {
    field.onChange(selectedValues);
    field.onBlur();
    setModalVisible(false);
  };

  const handleClose = () => {
    if (isMultiple) {
      // Restaura valores originais se cancelar
      setSelectedValues(field.value || []);
    }
    setTriggerRect(null);
    setModalVisible(false);
  };

  const getDisplayText = () => {
    if (isMultiple) {
      if (selectedValues.length === 0) {
        return placeholder;
      }
      if (selectedValues.length === 1) {
        const option = options.find((opt) => opt.value === selectedValues[0]);
        return option?.label || placeholder;
      }
      return `${selectedValues.length} itens selecionados`;
    } else {
      if (!selectedValue) {
        return placeholder;
      }
      const option = options.find((opt) => opt.value === selectedValue);
      return option?.label || placeholder;
    }
  };

  const isSelected = (value: string) => {
    if (isMultiple) {
      return selectedValues.includes(value);
    }
    return selectedValue === value;
  };

  const optionsScrollable = options.length > OPTIONS_WITHOUT_SCROLL_MAX;

  const renderOptionRow = (option: Option) => {
    const selected = isSelected(option.value);
    const blocked =
      isMultiple && !selected && maxSelected != null && selectedValues.length >= maxSelected;
    return (
      <Pressable
        key={option.value}
        onPress={() => handleSelect(option.value)}
        disabled={disabled || blocked}
        className={`mb-1 flex-row items-center rounded-xl px-3 py-3 ${
          selected ? 'bg-primary/10' : ''
        } ${disabled || blocked ? 'opacity-40' : ''}`}
      >
        <View className="flex-1 flex-row items-center">
          {isMultiple && (
            <View
              className={`mr-3 h-5 w-5 items-center justify-center rounded border-2 ${
                selected ? 'border-primary bg-primary' : 'border-neutral-400'
              }`}
            >
              {selected && <Ionicons name="checkmark" size={14} color="white" />}
            </View>
          )}
          {!isMultiple && selected && (
            <Ionicons name="checkmark-circle" size={20} className="mr-2 text-primary" />
          )}
          <Text
            style={[styles.optionText]}
            className={`flex-1 text-base ${
              selected ? 'font-semibold text-primary' : 'text-foreground'
            }`}
          >
            {option.label}
          </Text>
        </View>
      </Pressable>
    );
  };

  const renderOptionsList = () => {
    const rows = options.map(renderOptionRow);
    if (optionsScrollable) {
      return (
        <ScrollView
          keyboardShouldPersistTaps="handled"
          style={styles.optionsContainer}
          showsVerticalScrollIndicator
        >
          {rows}
        </ScrollView>
      );
    }
    return <View>{rows}</View>;
  };

  const renderModalPanel = (extraContentStyle?: object) => (
    <Pressable
      style={[
        optionsScrollable ? styles.modalContent : styles.modalContentCompact,
        extraContentStyle,
      ]}
      className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-lg"
      onPress={(e) => e.stopPropagation()}
    >
      <View className="mb-4 flex-row items-center justify-between border-b border-border pb-3">
        <ThemedText className="font-bold text-lg text-foreground">{label}</ThemedText>
        <Pressable onPress={handleClose}>
          <Ionicons name="close" size={24} className="text-foreground" />
        </Pressable>
      </View>

      {isMultiple && maxSelected != null ? (
        <ThemedText className="mb-2 text-xs text-neutral-500">
          {selectedValues.length >= maxSelected
            ? `Limite de ${maxSelected} atingido — desmarque um para trocar.`
            : `Escolha até ${maxSelected}.`}
        </ThemedText>
      ) : null}

      {renderOptionsList()}

      {isMultiple && (
        <View className="flex-row gap-2 border-t border-border pt-4">
          <Pressable
            onPress={handleClose}
            style={[styles.modalButton, styles.cancelButton]}
            className="border-border"
          >
            <Text style={[styles.modalButtonText]} className="text-foreground">
              Cancelar
            </Text>
          </Pressable>
          <Pressable
            onPress={handleConfirmMultiple}
            style={[styles.modalButton]}
            className="bg-primary"
          >
            <Text style={[styles.modalButtonText]} className="text-primary-foreground">
              Confirmar ({selectedValues.length})
            </Text>
          </Pressable>
        </View>
      )}
    </Pressable>
  );

  return (
    <View className="mb-6">
      <ThemedText type="labelInput" style={{ marginBottom: 6 }} className="text-neutral-700">
        {label}
      </ThemedText>
      <View ref={triggerRef} collapsable={false}>
        <Pressable
          onPress={async () => {
            if (disabled) return;
            // Fecha o teclado antes: senão as opções abrem atrás dele (e a
            // posição medida do gatilho muda quando ele fecha).
            await KeyboardController.dismiss();
            if (placement === 'top') {
              setTriggerRect(null);
              if (Platform.OS === 'web') {
                requestAnimationFrame(() => measureTriggerAndOpen());
              } else {
                setTimeout(() => measureTriggerAndOpen(), 50);
              }
            } else {
              setTriggerRect(null);
              setModalVisible(true);
            }
          }}
          disabled={disabled}
          style={[
            styles.selectButton,
            disabled && !noOpacityWhenDisabled ? styles.selectButtonDisabled : null,
          ]}
          testID={testID}
        >
          <Text
            style={[styles.selectText]}
            className={field.value ? 'text-foreground' : 'text-neutral-400'}
          >
            {getDisplayText()}
          </Text>
          <Ionicons
            name={modalVisible ? 'chevron-up' : 'chevron-down'}
            size={20}
            className="text-foreground"
          />
        </Pressable>
      </View>

      {/* Badges para múltiplas seleções */}
      {isMultiple && selectedValues.length > 0 && (
        <View className="mt-2 flex-row flex-wrap gap-2">
          {selectedValues.map((value) => {
            const option = options.find((opt) => opt.value === value);
            if (!option) return null;
            return (
              <View
                key={value}
                className="flex-row items-center rounded-full bg-primary/10 px-3 py-1.5"
                style={{ opacity: disabled && !noOpacityWhenDisabled ? 0.7 : 1 }}
              >
                <Text className="mr-1.5 font-semibold text-xs text-primary">{option.label}</Text>
                {!disabled && (
                  <Pressable onPress={() => handleSelect(value)}>
                    <Ionicons name="close-circle" size={16} className="text-primary" />
                  </Pressable>
                )}
              </View>
            );
          })}
        </View>
      )}

      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={handleClose}>
        <Pressable
          style={[
            styles.modalOverlay,
            (placement === 'center' || (placement === 'top' && !triggerRect)) &&
              styles.modalOverlayCenter,
          ]}
          onPress={handleClose}
        >
          {placement === 'top' && triggerRect ? (
            <View
              style={[
                styles.modalOverlayTopWrapper,
                {
                  paddingTop: insets.top,
                  paddingHorizontal: 20,
                  height: triggerRect.y,
                  bottom: undefined,
                },
              ]}
              pointerEvents="box-none"
            >
              {renderModalPanel({
                width: '100%',
                ...(optionsScrollable
                  ? { maxHeight: Math.max(200, triggerRect.y - insets.top - 16) }
                  : null),
              })}
            </View>
          ) : (
            renderModalPanel()
          )}
        </Pressable>
      </Modal>
    </View>
  );
}

const useStyles = createThemedStyles((th) =>
  StyleSheet.create({
    selectButton: {
      borderWidth: 1,
      borderColor: th('#E5E5E5'),
      borderRadius: 12,
      borderCurve: 'continuous',
      backgroundColor: th('#FFFFFF'),
      height: 48,
      paddingHorizontal: 16,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    selectButtonDisabled: {
      backgroundColor: th('#FAFAFA'),
      opacity: 0.7,
    },
    selectText: {
      fontSize: 16,
      fontFamily: FontPoppins.REGULAR,
      flex: 1,
    },
    badge: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      paddingVertical: 6,
      borderRadius: 16,
      borderWidth: 1,
    },
    badgeText: {
      fontSize: 12,
      fontFamily: FontPoppins.REGULAR,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      padding: 20,
      ...Platform.select({
        web: { minHeight: '100%' as const },
        default: {},
      }),
    },
    modalOverlayCenter: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    modalOverlayTopWrapper: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      justifyContent: 'flex-end',
    },
    modalContentTop: {
      position: 'absolute',
      left: 0,
      right: 0,
    },
    modalContent: {
      width: '100%',
      maxWidth: 400,
      maxHeight: '80%',
      borderRadius: 12,
      borderWidth: 1,
      padding: 20,
      ...Platform.select({
        ios: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
        },
        android: {
          elevation: 5,
        },
      }),
    },
    modalContentCompact: {
      width: '100%',
      maxWidth: 400,
      borderRadius: 12,
      borderWidth: 1,
      padding: 20,
      ...Platform.select({
        ios: {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 3.84,
        },
        android: {
          elevation: 5,
        },
      }),
    },
    optionsContainer: {
      maxHeight: 300,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderRadius: 4,
      borderWidth: 2,
      marginRight: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    optionText: {
      fontSize: 16,
      fontFamily: FontPoppins.REGULAR,
      flex: 1,
    },
    modalButton: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cancelButton: {
      borderWidth: 1,
      backgroundColor: 'transparent',
    },

    modalButtonText: {
      fontSize: 16,
      fontFamily: FontPoppins.SEMIBOLD,
    },
  }),
);
